import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faShoppingCart,
  faCreditCard,
  faSpinner,
  faExclamationTriangle,
  faUser,
  faMapMarkerAlt,
  faMinus,
  faPlus,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Footer from '../../Footer/Footer';
import { useBasket } from '../../../contexts/BasketContext';
import { woocommerceFarmService } from '../../../services/woocommerceFarmService';
import config from '../../../config/environment';

const CheckoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
  padding-top: 200px;
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding-top: 160px;
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 0.875rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 400px);
  gap: 3rem;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const CheckoutForm = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.light};
  border: 1px solid ${props => props.theme.colors.greyBackground};
  min-width: 0;
  box-sizing: border-box;
`;

const OrderSummary = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.light};
  border: 1px solid ${props => props.theme.colors.greyBackground};
  height: fit-content;
  position: sticky;
  top: 220px;
  min-width: 0;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    position: static;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const OrderItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.greyBackground};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemImage = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  background: ${props => props.theme.colors.greyBackground};
  flex-shrink: 0;
`;

const ItemImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ItemDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.h4`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  word-break: break-word;
  hyphens: auto;
`;

const ItemPrice = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
`;

const ItemQuantity = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const QuantityButton = styled.button`
  width: 24px;
  height: 24px;
  border: 1px solid ${props => props.theme.colors.greyBackground};
  background: white;
  color: ${props => props.theme.colors.primary};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    color: white;
    border-color: ${props => props.theme.colors.accent};
  }
  
  &:disabled {
    background: #f0f0f0;
    color: #ccc;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 40px;
  height: 24px;
  border: 1px solid ${props => props.theme.colors.greyBackground};
  text-align: center;
  font-size: 0.8rem;
  border-radius: 4px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #F44336;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #F44336;
    color: white;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  
  &.total {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 2px solid ${props => props.theme.colors.greyBackground};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.1);
  }
  
  &.error {
    border-color: #F44336;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem 1rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.1);
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary};
  line-height: 1.5;
  padding: 0.5rem 0;
  
  input[type="checkbox"] {
    margin-top: 0.2rem;
    cursor: pointer;
    width: 18px;
    height: 18px;
    min-width: 18px;
    accent-color: ${props => props.theme.colors.accent};
    
    &:focus {
      outline: 2px solid ${props => props.theme.colors.accent};
      outline-offset: 2px;
    }
  }
  
  span {
    flex: 1;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #F44336;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 1.2rem 2rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.light};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
`;

const FarmShopCheckout = () => {
  const navigate = useNavigate();
  const { items, totalItems, totalPrice, updateQuantity, removeFromBasket, clearBasket } = useBasket();
  const [stripePromise] = useState(() =>
    loadStripe(process.env.REACT_APP_FARM_STRIPE_PK || process.env.REACT_APP_STRIPE_PK || '', {
      locale: 'en-GB'
    })
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    notes: '',
    pickupDate: '',
    pickupNotes: '',
    marketingOptIn: false
  });
  const minPickupDate = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);

  const appearance = useMemo(
    () => ({
      theme: 'stripe',
      variables: {
        colorPrimary: '#9d6d49',
        borderRadius: '8px'
      }
    }),
    []
  );

  const elementsOptions = useMemo(() => {
    if (!clientSecret) return undefined;
    return {
      clientSecret,
      appearance
    };
  }, [clientSecret, appearance]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (totalItems === 0) navigate('/farm-shop');
  }, [totalItems, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.pickupDate) newErrors.pickupDate = 'Please choose your collection date';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePaymentClick = async () => {
    if (!validateForm()) {
      setOrderError('Please fill in all required fields correctly');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsProcessing(true);
    setOrderError(null);
    setClientSecret('');
    setCreatedOrderId(null);
    setIsCreatingOrder(true);

    let newOrderId = null;
    let consoleGroupActive = false;

    try {
      if (typeof console !== 'undefined' && console.groupCollapsed) {
        console.groupCollapsed('FarmShop Checkout: initialise payment');
        consoleGroupActive = true;
      }

      const lineItems = items.map(item => ({
        product_id: item.id,
        quantity: item.quantity,
        variation_id: item.variation_id || undefined,
        variation: item.variation || undefined
      }));
      
      const billing = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address_1: formData.address,
        city: formData.city,
        postcode: formData.postcode,
        country: 'GB'
      };
      
      const orderPayload = {
        payment_method: 'stripe',
        payment_method_title: 'Credit Card (Stripe)',
        set_paid: false,
        billing,
        shipping: billing,
        line_items: lineItems,
        customer_note: formData.notes || '',
        meta_data: [
          { key: '_stripe_pending', value: 'yes' },
          { key: '_stripe_customer_email', value: formData.email },
          { key: '_farm_pickup_date', value: formData.pickupDate },
          { key: '_farm_pickup_notes', value: formData.pickupNotes || '' }
        ],
        shipping_lines: [
          {
            method_id: 'local_pickup',
            method_title: 'Click & Collect',
            total: '0.00'
          }
        ]
      };

      // Add coupon if applied
      if (appliedCoupon && appliedCoupon.code) {
        orderPayload.coupon_lines = [
          {
            code: appliedCoupon.code
          }
        ];
      }

      if (consoleGroupActive) {
        console.debug('FarmShop Checkout · Line items', lineItems);
        console.debug('FarmShop Checkout · Billing', billing);
        console.debug('FarmShop Checkout · Order payload', orderPayload);
      }

      const order = await woocommerceFarmService.createOrder(orderPayload);
      newOrderId = order.id;

      if (consoleGroupActive) {
        console.debug('FarmShop Checkout · Order created', { orderId: newOrderId, status: order.status });
      }

      const proxyUrl =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? '/api/farm-create-payment-intent.php'
        : 'https://www.belvoircastle.com/api/farm-create-payment-intent.php';
      
      const intentPayload = {
        order_id: newOrderId,
        customer_email: billing.email,
        description: `Farm Shop Order #${newOrderId}`
      };

      const stripeResponse = await fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(intentPayload)
      });
      
      if (!stripeResponse.ok) {
        const responseBody = await stripeResponse.text();
        let parsedBody = null;
        try {
          parsedBody = JSON.parse(responseBody);
        } catch (_) {
          parsedBody = null;
        }

        if (consoleGroupActive) {
          console.error('FarmShop Checkout · Payment intent API error', {
            status: stripeResponse.status,
            body: parsedBody || responseBody
          });
        }

        const error = new Error(
          parsedBody?.error || parsedBody?.message || responseBody || 'Failed to initialise payment'
        );
        error.status = stripeResponse.status;
        error.apiResponse = parsedBody || responseBody;
        throw error;
      }

      const paymentIntent = await stripeResponse.json();

      if (consoleGroupActive) {
        console.debug('FarmShop Checkout · Payment intent created', {
          paymentIntentId: paymentIntent?.id,
          status: paymentIntent?.status
        });
      }

      if (!paymentIntent?.client_secret) {
        const error = new Error('Payment intent missing client secret');
        error.apiResponse = paymentIntent;
        throw error;
      }

      if (isMountedRef.current) {
        setCreatedOrderId(newOrderId);
        setClientSecret(paymentIntent.client_secret);
      }
    } catch (error) {
      console.error('FarmShop Checkout · Initialisation failed', error);

      if (newOrderId) {
        try {
          await woocommerceFarmService.updateOrderStatus(newOrderId, { status: 'cancelled' });
          if (consoleGroupActive) {
            console.debug('FarmShop Checkout · Cancelled order after failure', { orderId: newOrderId });
          }
        } catch (cancelError) {
          console.error('FarmShop Checkout · Failed to cancel pending order', cancelError);
        }
      }

      const statusPart = error?.status ? ` (HTTP ${error.status})` : '';
      const message = error?.message || 'We could not initialise the payment page';
      const orderPart = newOrderId ? ` Order #${newOrderId} has been cancelled and no payment was taken.` : ' No payment was taken.';

      if (isMountedRef.current) {
        setOrderError(`Payment initialisation failed${statusPart}: ${message}.${orderPart}`);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      if (consoleGroupActive && typeof console !== 'undefined' && console.groupEnd) {
        console.groupEnd();
      }
      if (isMountedRef.current) {
        setIsProcessing(false);
        setIsCreatingOrder(false);
      }
    }
  };

  const shipping = 0;
  const subtotal = totalPrice;
  const discount = discountAmount;
  const total = Math.max(0, subtotal - discount + shipping);
  const shippingLabel = 'Click & Collect (Free)';

  // Validate and apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsValidatingCoupon(true);
    setCouponError(null);

    try {
      const coupon = await woocommerceFarmService.validateCoupon(couponCode.trim());
      
      // Calculate discount amount (10% for percentage coupons)
      let calculatedDiscount = 0;
      if (coupon.discount_type === 'percent') {
        calculatedDiscount = (subtotal * parseFloat(coupon.amount)) / 100;
      } else if (coupon.discount_type === 'fixed_cart') {
        calculatedDiscount = parseFloat(coupon.amount);
      } else if (coupon.discount_type === 'fixed_product') {
        // For fixed product, apply to each item (simplified - applies to total)
        calculatedDiscount = parseFloat(coupon.amount);
      }

      setAppliedCoupon(coupon);
      setDiscountAmount(Math.min(calculatedDiscount, subtotal)); // Don't discount more than subtotal
      setCouponError(null);
    } catch (error) {
      setCouponError(error.message || 'Invalid coupon code');
      setAppliedCoupon(null);
      setDiscountAmount(0);
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  // Remove coupon
  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponError(null);
    setDiscountAmount(0);
  };

  return (
    <CheckoutContainer>
      <ContentContainer>
        <BackButton onClick={() => navigate('/farm-shop')}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Farm Shop
        </BackButton>

        <CheckoutGrid>
          <CheckoutForm>
            <div>
              {orderError && (
                <ErrorMessage>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{orderError}</span>
                </ErrorMessage>
              )}
              <SectionTitle>
                <FontAwesomeIcon icon={faUser} />
                Contact Information
              </SectionTitle>
              <Row>
                <FormGroup>
                  <Label>First Name *</Label>
                  <Input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={errors.firstName ? 'error' : ''}
                  />
                  {errors.firstName && (
                    <span style={{ color: '#F44336', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      {errors.firstName}
                    </span>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>Last Name *</Label>
                  <Input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={errors.lastName ? 'error' : ''}
                  />
                  {errors.lastName && (
                    <span style={{ color: '#F44336', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      {errors.lastName}
                    </span>
                  )}
                </FormGroup>
              </Row>
              <Row>
                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && (
                    <span style={{ color: '#F44336', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      {errors.email}
                    </span>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={errors.phone ? 'error' : ''}
                  />
                  {errors.phone && (
                    <span style={{ color: '#F44336', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      {errors.phone}
                    </span>
                  )}
                </FormGroup>
              </Row>

              <SectionTitle>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                Shipping Address
              </SectionTitle>
              <FormGroup>
                <Label>Address *</Label>
                <Input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={errors.address ? 'error' : ''}
                />
                {errors.address && (
                  <span style={{ color: '#F44336', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    {errors.address}
                  </span>
                )}
              </FormGroup>
              <Row>
                <FormGroup>
                  <Label>City *</Label>
                  <Input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={errors.city ? 'error' : ''}
                  />
                  {errors.city && (
                    <span style={{ color: '#F44336', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      {errors.city}
                    </span>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>Postcode *</Label>
                  <Input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    className={errors.postcode ? 'error' : ''}
                  />
                  {errors.postcode && (
                    <span style={{ color: '#F44336', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      {errors.postcode}
                    </span>
                  )}
                </FormGroup>
              </Row>
              <FormGroup>
                <Label>Country</Label>
                <Input type="text" name="country" value={formData.country} onChange={handleInputChange} disabled />
              </FormGroup>
              <FormGroup>
                <Label>Order Notes (Optional)</Label>
                <TextArea name="notes" value={formData.notes} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup>
                <CheckboxContainer>
                  <input
                    type="checkbox"
                    name="marketingOptIn"
                    checked={formData.marketingOptIn}
                    onChange={(e) => setFormData(prev => ({ ...prev, marketingOptIn: e.target.checked }))}
                  />
                  <span>
                    I would like to receive marketing communications about special offers, new products, and events from Belvoir Farm Shop
                  </span>
                </CheckboxContainer>
              </FormGroup>
              <SectionTitle style={{ marginTop: '2rem' }}>
                <FontAwesomeIcon icon={faCreditCard} />
                Discount Code
              </SectionTitle>
              <FormGroup>
                {!appliedCoupon ? (
                  <>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <Input
                        type="text"
                        placeholder="Enter discount code"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value.toUpperCase());
                          setCouponError(null);
                        }}
                        style={{ flex: 1 }}
                        disabled={isValidatingCoupon}
                      />
                      <CheckoutButton
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={isValidatingCoupon || !couponCode.trim()}
                        style={{ marginTop: 0, padding: '0.8rem 1.5rem', width: 'auto' }}
                      >
                        {isValidatingCoupon ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          'Apply'
                        )}
                      </CheckoutButton>
                    </div>
                    {couponError && (
                      <span style={{ color: '#F44336', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                        {couponError}
                      </span>
                    )}
                  </>
                ) : (
                  <div style={{ 
                    background: '#e8f5e9', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <div>
                      <strong style={{ color: '#2e7d32' }}>Code Applied: {appliedCoupon.code}</strong>
                      <div style={{ fontSize: '0.9rem', color: '#555', marginTop: '0.25rem' }}>
                        {appliedCoupon.discount_type === 'percent' 
                          ? `${appliedCoupon.amount}% off` 
                          : `£${parseFloat(appliedCoupon.amount).toFixed(2)} off`}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#F44336',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        fontSize: '0.9rem',
                        textDecoration: 'underline'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </FormGroup>
              <SectionTitle style={{ marginTop: '2rem' }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                Click & Collect
              </SectionTitle>
              <FormGroup>
                <Label>Collection Date *</Label>
                <Input
                  type="date"
                  name="pickupDate"
                  value={formData.pickupDate}
                  onChange={handleInputChange}
                  className={errors.pickupDate ? 'error' : ''}
                  min={minPickupDate}
                />
                {errors.pickupDate && (
                  <span style={{ color: '#F44336', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    {errors.pickupDate}
                  </span>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Collection Notes (Optional)</Label>
                <TextArea
                  name="pickupNotes"
                  value={formData.pickupNotes}
                  onChange={handleInputChange}
                  placeholder="Let us know a preferred time"
                />
              </FormGroup>
              <p style={{ marginTop: '0.5rem', color: '#6b5b4f', fontSize: '0.9rem' }}>
                Collections are from the Belvoir Farm Shop. We will email when your
                order is ready to collect &ndash; please bring your confirmation email.
              </p>

              {!clientSecret && !isProcessing && (
                <CheckoutButton type="button" onClick={handlePaymentClick} disabled={isProcessing}>
                  <FontAwesomeIcon icon={faCreditCard} />
                  Proceed to Secure Payment - £{total.toFixed(2)}
                  {appliedCoupon && discount > 0 && (
                    <span style={{ fontSize: '0.9rem', opacity: 0.9, marginLeft: '0.5rem' }}>
                      (Save £{discount.toFixed(2)})
                    </span>
                  )}
                </CheckoutButton>
              )}

              {isProcessing && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    style={{ fontSize: '2rem', color: '#9d6d49', marginBottom: '1rem' }}
                  />
                  <div>{isCreatingOrder ? 'Creating order...' : 'Preparing payment...'}</div>
                </div>
              )}

              {clientSecret && stripePromise && elementsOptions && (
                <Elements stripe={stripePromise} options={elementsOptions}>
                  <CardPaymentForm
                    clientSecret={clientSecret}
                    createdOrderId={createdOrderId}
                    billingDetails={formData}
                    totalAmount={total}
                    shipping={shipping}
                    clearBasket={clearBasket}
                    setOrderError={setOrderError}
                    setClientSecret={setClientSecret}
                    setCreatedOrderId={setCreatedOrderId}
                    setIsProcessing={setIsProcessing}
                    setIsCreatingOrder={setIsCreatingOrder}
                    marketingOptIn={formData.marketingOptIn}
                  />
                  </Elements>
              )}
            </div>
          </CheckoutForm>

          <OrderSummary>
            <SectionTitle>
              <FontAwesomeIcon icon={faShoppingCart} />
              Order Summary
            </SectionTitle>
            {items.map(item => (
              <OrderItem key={item.id}>
                <ItemImage>
                  <ItemImg
                    src={item.image}
                    alt={item.name}
                    onError={(e) => {
                      e.currentTarget.src = '/placeholder-product.jpg';
                    }}
                  />
                </ItemImage>
                <ItemDetails>
                  <ItemName>{item.name}</ItemName>
                  <ItemPrice>£{item.price.toFixed(2)}</ItemPrice>
                  <ItemQuantity>
                    <QuantityButton onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))} disabled={item.quantity <= 1}>
                      <FontAwesomeIcon icon={faMinus} />
                    </QuantityButton>
                    <QuantityInput
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.id, Math.max(1, parseInt(e.target.value, 10) || 1))}
                      min="1"
                      max="10"
                    />
                    <QuantityButton onClick={() => updateQuantity(item.id, Math.min(10, item.quantity + 1))} disabled={item.quantity >= 10}>
                      <FontAwesomeIcon icon={faPlus} />
                    </QuantityButton>
                    <RemoveButton onClick={() => removeFromBasket(item.id)} title="Remove item">
                      <FontAwesomeIcon icon={faTrash} />
                    </RemoveButton>
                  </ItemQuantity>
                </ItemDetails>
              </OrderItem>
            ))}

            <SummaryRow>
              <span>Subtotal</span>
              <span>£{subtotal.toFixed(2)}</span>
            </SummaryRow>

            {appliedCoupon && discount > 0 && (
              <SummaryRow style={{ color: '#2e7d32' }}>
                <span>Discount ({appliedCoupon.code})</span>
                <span>-£{discount.toFixed(2)}</span>
              </SummaryRow>
            )}

            <SummaryRow>
              <span>Collection</span>
              <span>{shippingLabel}</span>
            </SummaryRow>

            <SummaryRow className="total">
              <span>Total</span>
              <span>£{total.toFixed(2)}</span>
            </SummaryRow>
          </OrderSummary>
        </CheckoutGrid>
      </ContentContainer>
      <Footer />
    </CheckoutContainer>
  );
};

export default FarmShopCheckout;

const CardFormContainer = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  border: 1px solid ${props => props.theme.colors.greyBackground};
  border-radius: 10px;
  background: #faf8f5;
`;

const CardLabel = styled.div`
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${props => props.theme.colors.primary};
`;

const StyledCardElement = styled(CardElement)`
  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  background: white;
`;

const PayButton = styled(CheckoutButton)`
  margin-top: 1.5rem;
`;

const CardPaymentForm = ({
  clientSecret,
  createdOrderId,
  billingDetails,
  totalAmount,
  shipping,
  clearBasket,
  setOrderError,
  setClientSecret,
  setCreatedOrderId,
  setIsProcessing,
  setIsCreatingOrder,
  marketingOptIn
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleCardPayment = async () => {
    if (!stripe || !elements || !clientSecret || !createdOrderId) return;

    setIsSubmitting(true);
    setIsProcessing(true);
    setIsCreatingOrder(false);
    setOrderError(null);

    let consoleGroupActive = false;

    try {
      if (typeof console !== 'undefined' && console.groupCollapsed) {
        console.groupCollapsed('FarmShop Checkout: confirm card payment');
        consoleGroupActive = true;
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Unable to load card input');

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${billingDetails.firstName} ${billingDetails.lastName}`,
            email: billingDetails.email,
            phone: billingDetails.phone,
            address: {
              line1: billingDetails.address,
              city: billingDetails.city,
              postal_code: billingDetails.postcode,
              country: 'GB'
            }
          }
        },
        setup_future_usage: 'off_session'
      });

      if (confirmError) {
        if (consoleGroupActive) {
          console.error('FarmShop Checkout · confirmCardPayment error', confirmError);
        }
        if (isMountedRef.current) {
          setOrderError(confirmError.message || 'Payment failed');
        }
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        try {
          await woocommerceFarmService.updateOrderStatus(createdOrderId, {
            status: 'processing',
            set_paid: true,
            transaction_id: paymentIntent.id
          });
        } catch (updateError) {
          console.error('FarmShop Checkout · Failed to update order status after payment', updateError);
        }

        // Subscribe to Mailchimp if marketing opt-in is checked
        if (marketingOptIn && billingDetails.email && billingDetails.firstName && billingDetails.lastName) {
          try {
            const subscribeResponse = await fetch(`${config.apiEndpoint}/newsletter/v1/api/subscribe`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              mode: 'cors',
              body: JSON.stringify({
                firstName: billingDetails.firstName,
                lastName: billingDetails.lastName,
                email: billingDetails.email,
                origin: window.location.origin,
                tags: ['farmshop']
              })
            });

            if (subscribeResponse.ok) {
              if (consoleGroupActive) {
                console.debug('FarmShop Checkout · Successfully subscribed to Mailchimp', {
                  email: billingDetails.email,
                  tags: ['farmshop']
                });
              }
            } else {
              // Log but don't block the checkout flow if Mailchimp subscription fails
              console.warn('FarmShop Checkout · Mailchimp subscription failed (non-blocking)', {
                status: subscribeResponse.status,
                email: billingDetails.email
              });
            }
          } catch (mailchimpError) {
            // Log but don't block the checkout flow if Mailchimp subscription fails
            console.warn('FarmShop Checkout · Mailchimp subscription error (non-blocking)', mailchimpError);
          }
        }

        clearBasket();
        window.location.href = `/farm-shop/checkout/success?order_id=${createdOrderId}&total=${totalAmount.toFixed(
          2
        )}&shipping=${shipping.toFixed(2)}`;
      } else if (paymentIntent) {
        const message = `Unexpected payment status: ${paymentIntent.status}`;
        if (isMountedRef.current) setOrderError(message);
      }
    } catch (error) {
      console.error('FarmShop Checkout · Payment failed', error);
      if (isMountedRef.current) {
        setOrderError(error.message || 'Payment failed');
      }
    } finally {
      if (consoleGroupActive && typeof console !== 'undefined' && console.groupEnd) {
        console.groupEnd();
      }
      if (isMountedRef.current) {
        setIsSubmitting(false);
        setIsProcessing(false);
        setIsCreatingOrder(false);
      }
    }
  };

  useEffect(() => {
    if (!clientSecret) {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [clientSecret]);

  return (
    <CardFormContainer>
      <CardLabel>Card details</CardLabel>
      <StyledCardElement
        options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#333',
              '::placeholder': { color: '#9d6d49' }
            },
            invalid: { color: '#F44336' }
          },
          hidePostalCode: true,
          disableLink: true
        }}
      />
      <PayButton
        type="button"
        onClick={handleCardPayment}
        disabled={!stripe || isSubmitting}
      >
        {isSubmitting ? (
          <>
            <FontAwesomeIcon icon={faSpinner} spin />
            Processing Payment...
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faCreditCard} />
            Pay £{totalAmount.toFixed(2)}
          </>
        )}
      </PayButton>
    </CardFormContainer>
  );
};

